import { supabase, SUPABASE_URL } from '../supabaseClient';
import { IMAGE_WEBP_EXT, IMAGE_WEBP_MIME } from '../utils/imageCompress';
import { notifySlideImageStorageChanged, type SlideImageStorageUsage } from './slideImageStorageService';

const BUCKET = 'assignment-cover-images';
const CACHE_CONTROL = '31536000';

function genStorageFileName(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return `${id}.${IMAGE_WEBP_EXT}`;
  return `${Date.now()}-${Math.random().toString(36).slice(2)}.${IMAGE_WEBP_EXT}`;
}

export function isAssignmentCoverImageStoragePublicUrl(publicUrl: string): boolean {
  if (!publicUrl.trim() || !SUPABASE_URL) return false;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  return publicUrl.trim().startsWith(prefix);
}

export function storagePathFromAssignmentCoverImagePublicUrl(
  publicUrl: string,
  userId: string
): string | null {
  if (!publicUrl.trim() || !SUPABASE_URL) return null;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  if (!publicUrl.startsWith(prefix)) return null;
  const path = decodeURIComponent(publicUrl.slice(prefix.length).split('?')[0] ?? '');
  if (!path.startsWith(`${userId}/`)) return null;
  return path;
}

/** 과제 커버 이미지 저장 용량(사용자 통합 이미지 할당량을 공유 — 이용권별 한도) 조회 */
export async function fetchAssignmentCoverImageStorageUsage(): Promise<SlideImageStorageUsage | null> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) return null;
  const { data, error } = await supabase.rpc('get_user_image_storage_usage');
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    bytesUsed: Number((row as { bytes_used?: number })?.bytes_used ?? 0),
    bytesLimit: Number((row as { bytes_limit?: number })?.bytes_limit ?? 104857600),
  };
}

export async function assertAssignmentCoverQuota(blobSize: number): Promise<void> {
  const usage = await fetchAssignmentCoverImageStorageUsage();
  if (usage && usage.bytesUsed + blobSize > usage.bytesLimit) {
    throw new Error('QUOTA_EXCEEDED');
  }
}

/** 과제 커버는 assignment-cover-images 버킷(사용자별 폴더)에 저장, 통합 할당량에 함께 집계 */
export async function uploadAssignmentCoverImage(blob: Blob): Promise<{ path: string; url: string }> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (userErr || !userId) throw new Error('로그인이 필요합니다.');

  await assertAssignmentCoverQuota(blob.size);

  const fileName = genStorageFileName();
  const storagePath = `${userId}/${fileName}`;

  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: IMAGE_WEBP_MIME,
    cacheControl: CACHE_CONTROL,
    upsert: false,
  });
  if (uploadErr) throw uploadErr;

  try {
    const { error: regErr } = await supabase.rpc('register_assignment_cover_image_asset', {
      p_path: storagePath,
      p_bytes: blob.size,
    });
    if (regErr) {
      await supabase.storage.from(BUCKET).remove([storagePath]);
      if (String(regErr.message ?? '').includes('QUOTA_EXCEEDED')) {
        throw new Error('QUOTA_EXCEEDED');
      }
      throw regErr;
    }
  } catch (e) {
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw e;
  }

  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const publicUrl = urlData.publicUrl;
  if (!publicUrl) throw new Error('이미지 URL을 만들지 못했습니다.');

  notifySlideImageStorageChanged();
  return { path: storagePath, url: publicUrl };
}

export async function deleteAssignmentCoverImage(storagePath: string): Promise<void> {
  const path = storagePath.trim();
  if (!path) return;

  try {
    await supabase.rpc('release_assignment_cover_image_asset', { p_path: path });
  } catch {
    /* 메타 없으면 무시 */
  }

  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error && !/not.?found|Object not found/i.test(error.message ?? '')) {
    throw error;
  }
  notifySlideImageStorageChanged();
}

export function newAssignmentCoverImageId(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return id;
  return `acov-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export { IMAGE_WEBP_EXT };
